const stripBoldTitle = (line = "") => line.replace(/^\*\*|\*\*$/g, "").trim();

const cleanLines = (text = "") =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const stripTitleLines = (lines = []) => {
  const idx = lines.findIndex((line) => /^\*\*.*\*\*$/.test(line));
  return idx === -1 ? lines : lines.slice(idx + 1);
};

const normalizeHeading = (line = "") =>
  line.replace(/^#+\s*/, "").replace(/:+$/, "").trim().toLowerCase();

const extractTitle = (markdown = "") => {
  const lines = String(markdown || "").split(/\r?\n/);
  for (const line of lines) {
    if (/^\*\*.*\*\*$/.test(line.trim())) {
      return stripBoldTitle(line.trim());
    }
  }
  const first = lines.find((l) => l.trim().length > 0) || "";
  return first.replace(/^#+\s*/, "").trim();
};

const stripBullet = (line = "") =>
  line
    .replace(/^[-*]\s+/, "")
    .replace(/^\d+[).]\s+/, "")
    .replace(/^[\u2022•]\s+/, "")
    .trim();

const isTableLine = (line = "") => /[|│]/.test(line) && /[A-Za-z0-9]/.test(line);

const normalizeTableLine = (line = "") => line.replace(/│/g, "|");

const parsePipeTable = (lines = []) => {
  const cleaned = lines
    .map((line) => normalizeTableLine(line).replace(/\s+/g, " ").trim())
    .filter((line) => line.length > 0);

  if (!cleaned.length) return null;

  const rows = cleaned.map((line) =>
    normalizeTableLine(line)
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 0)
      .map((cell) => cell.replace(/^#\s*/, ""))
  );

  if (!rows.length) return null;

  const headerRow = rows[0];
  const looksLikeHeader = headerRow.some((cell) =>
    /metric|value|orders|revenue|ratio|score|dimension|weight|status|channel/i.test(cell)
  );

  return {
    headers: looksLikeHeader ? headerRow : null,
    rows: looksLikeHeader ? rows.slice(1) : rows,
  };
};

const extractTableBlock = (lines = []) => {
  let start = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (isTableLine(lines[i])) {
      start = i;
      break;
    }
  }
  if (start === -1) return { table: null, before: lines, after: [] };

  let end = start;
  while (end < lines.length && isTableLine(lines[end])) {
    end += 1;
  }

  const before = lines.slice(0, start);
  const tableLines = lines.slice(start, end);
  const after = lines.slice(end);

  return { table: parsePipeTable(tableLines), before, after };
};

const splitByHeading = (lines = [], heading = "What Does This Mean?") => {
  const target = normalizeHeading(heading);
  const idx = lines.findIndex(
    (line) => normalizeHeading(line).startsWith(target)
  );
  if (idx === -1) return { lead: lines, tail: [] };
  return { lead: lines.slice(0, idx), tail: lines.slice(idx + 1) };
};

const toBullets = (lines = []) =>
  lines
    .filter(
      (line) =>
        /^[-*]\s+/.test(line) ||
        /^\d+[).]\s+/.test(line) ||
        /^[\u2022•]\s+/.test(line)
    )
    .map(stripBullet);

const toParagraphs = (lines = []) =>
  lines
    .filter(
      (line) =>
        !/^[-*]\s+/.test(line) &&
        !/^\d+[).]\s+/.test(line) &&
        !/^[\u2022•]\s+/.test(line) &&
        !isTableLine(line)
    )
    .map(stripBullet);

const extractAnalysisPeriod = (lines = []) => {
  const line = lines.find((l) => l.toLowerCase().startsWith("analysis period"));
  return line ? line.replace(/^analysis period:\s*/i, "") : null;
};

const extractVerdict = (lines = []) => {
  if (!lines.length) return null;
  const last = lines[lines.length - 1];
  if (!last) return null;
  if (/what does this mean/i.test(last)) return null;
  if (last.includes("|")) return null;
  return last.length <= 140 ? last : null;
};

const splitSkuBlocks = (lines = []) => {
  const blocks = [];
  let current = null;
  lines.forEach((line) => {
    if (/^sku\s+\d+/i.test(line)) {
      if (current) blocks.push(current);
      current = { title: line.trim(), lines: [] };
      return;
    }
    if (current) {
      current.lines.push(line);
    }
  });
  if (current) blocks.push(current);
  return blocks;
};

export {
  cleanLines,
  stripTitleLines,
  extractTitle,
  extractTableBlock,
  parsePipeTable,
  stripBullet,
  toBullets,
  toParagraphs,
  splitByHeading,
  extractAnalysisPeriod,
  extractVerdict,
  splitSkuBlocks,
};
